import React from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { FormattedMessage } from '~/techstyle-shared/react-intl';

import { LAYOUT_TYPE } from '../../../constants';
import { getFacetOptionRBKey } from '../../../utils/filters';

import DisabledOptionWrapper from './DisabledOptionWrapper';

const DynamicFormattedMessage = FormattedMessage;

const FilterOptionsList = styled.ul<{ $useColumnLayout: boolean }>`
  list-style: none;
  display: flex;
  flex-direction: ${({ $useColumnLayout }) =>
    $useColumnLayout ? 'column' : 'row'};
  flex-wrap: wrap;
  gap: ${({ $useColumnLayout }) => ($useColumnLayout ? '24px' : '8px')};
`;

const FilterOption = styled.button`
  min-width: 64px;
  padding: 12px 16px;
  border-radius: 4px;
  border: 1px solid transparent;
  cursor: pointer;
  transition:
    border-color 0.2s,
    background-color 0.2s;
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
  font-size: 14px;

  /* Gate hover and active states on hover-capable devices — on touch,
     :active can linger after tap when the DOM updates mid-tap, leaving the
     option stuck in its press style. */
  @media (hover: hover) {
    &:hover {
      border-color: ${({ theme }) =>
        theme.colors.gridV2UnselectedFilterBtnBorderHover};
    }

    &:active {
      border-color: ${({ theme }) =>
        theme.colors.gridV2UnselectedFilterBtnBorderActive};
    }
  }

  &[aria-selected='true'] {
    border-color: ${({ theme }) => theme.colors.gridV2SelectedFilterBtnBorder};
    font-weight: 700;

    @media (hover: hover) {
      &:hover {
        background-color: ${({ theme }) =>
          theme.colors.gridV2SelectedFilterBtnBgHover};
      }

      &:active {
        background-color: ${({ theme }) =>
          theme.colors.gridV2SelectedFilterBtnBgActive};
      }
    }
  }
`;

const FilterOptionsDefaultBtn = ({
  facetName,
  options,
  onClick,
  isSelected,
  isDisabled,
  layoutType = LAYOUT_TYPE.FILTER_PANEL,
}) => {
  return (
    <FilterOptionsList
      $useColumnLayout={layoutType === LAYOUT_TYPE.FILTER_PANEL}
    >
      {options.map((option) => {
        const facetValue = option.value;
        const selected = isSelected(facetValue);
        const disabled = isDisabled?.(facetValue);

        return (
          <DisabledOptionWrapper
            key={facetValue}
            $disabled={disabled}
            aria-disabled={disabled || undefined}
          >
            <FilterOption
              onClick={() => onClick(facetValue)}
              aria-selected={selected}
              data-autotag={`filter-${facetName}-option`}
            >
              <DynamicFormattedMessage
                id={`grid_dynamic.${getFacetOptionRBKey(
                  facetName,
                  facetValue
                )}`}
                defaultMessage={option.display_name}
              />
            </FilterOption>
          </DisabledOptionWrapper>
        );
      })}
    </FilterOptionsList>
  );
};

FilterOptionsDefaultBtn.propTypes = {
  facetName: PropTypes.string.isRequired,
  isDisabled: PropTypes.func,
  isSelected: PropTypes.func.isRequired,
  layoutType: PropTypes.oneOf(Object.values(LAYOUT_TYPE)),
  onClick: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      display_name: PropTypes.string,
      value: PropTypes.string,
    })
  ).isRequired,
};

export default FilterOptionsDefaultBtn;
